import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { exchangeSocialCode, PROVIDER_NAMES } from '../api/socialAuth';
import { PROFILE_INCOMPLETE_KEY } from '../api/profileCompletion';

// /auth/social?code=…&provider=… — finishes "Continue with …": swaps the one-time code for the
// usual login response and stores it exactly like the email/password login does.
const SocialCallback = () => {
  const { t } = useTranslation('auth');
  const [error, setError] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // React strict mode runs effects twice; the code is single-use
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    // Drop the code from the address bar and history
    window.history.replaceState(null, '', window.location.pathname);

    if (!code) {
      setError(t('social_err_failed', { defaultValue: 'Sign-in failed. Please try again.', provider: '' }));
      return;
    }

    exchangeSocialCode(code)
      .then((data) => {
        const token = data?.success?.token;
        if (!token) throw new Error(t('social_err_failed', { defaultValue: 'Sign-in failed. Please try again.', provider: '' }));
        localStorage.setItem('auth_token', token);
        if (data.Profile) localStorage.setItem('user_profile', JSON.stringify(data.Profile));
        if (data.UserDetails) localStorage.setItem('user_details', JSON.stringify(data.UserDetails));
        localStorage.setItem('login_response_full', JSON.stringify(data));
        // New social accounts have only a name and email: ask for the required details first
        if (data.profileComplete === false) {
          localStorage.setItem(PROFILE_INCOMPLETE_KEY, '1');
          window.location.href = '/complete-profile';
          return;
        }
        localStorage.removeItem(PROFILE_INCOMPLETE_KEY);
        window.location.href = '/dashboard';
      })
      .catch((err) => setError(err.message));
  }, [t]);

  const provider = PROVIDER_NAMES[new URLSearchParams(window.location.search).get('provider')] || '';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'inherit' }}>
      <div style={{ maxWidth: 420, width: '100%', textAlign: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '32px 24px', boxShadow: '0 10px 30px rgba(15,23,42,.08)' }}>
        {error ? (
          <>
            <div style={{ fontSize: 34, marginBottom: 8 }}>⚠️</div>
            <p style={{ color: '#0f172a', fontSize: 15, marginBottom: 20 }}>{error}</p>
            <a href="/" style={{ display: 'inline-block', padding: '10px 20px', borderRadius: 8, background: '#185FA5', color: '#fff', textDecoration: 'none', fontWeight: 600 }}>
              {t('social_back_to_login', 'Back to sign in')}
            </a>
          </>
        ) : (
          <>
            <style>{'@keyframes social-cb-spin{to{transform:rotate(360deg)}}'}</style>
            <div
              aria-hidden="true"
              style={{ width: 36, height: 36, margin: '0 auto 16px', border: '3px solid #e2e8f0', borderTopColor: '#185FA5', borderRadius: '50%', animation: 'social-cb-spin .8s linear infinite' }}
            />
            <p style={{ color: '#475569', fontSize: 15, margin: 0 }}>
              {t('social_signing_in', { defaultValue: 'Signing you in{{with}}…', with: provider ? ` with ${provider}` : '' })}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SocialCallback;
